import { SEGMENT_COUNT_MAX, SEGMENT_HARD_MAX } from "./device-manager/lookups";
import { WIZARD_IDLE_TIMEOUT_MS } from "./timing-constants";
import { deviceLabel, type GoveeDevice } from "./types";
import { readDeviceBaseline, restoreSegmentsGrouped } from "./device-baseline";
import { resolveLabel, type I18nKey } from "./i18n";

/**
 * Interpolate {name} placeholders against a params object.
 *
 * @param template Message template with `{placeholder}` slots
 * @param params Values to substitute — keys must match the placeholder names
 */
function format(template: string, params?: Record<string, string | number>): string {
  if (!params) {
    return template;
  }
  return template.replace(/\{(\w+)\}/g, (m, key: string) => (key in params ? String(params[key]) : m));
}

/**
 * Deliberately terse responses: the only client is the React admin component,
 * which renders the grid from `snapshot` plus the `error` / `active` / `done`
 * / `aborted` / `applied` flags and carries its own translations. The
 * step-by-step prose the pre-React (sendTo-button) UI showed — "segment N is
 * now lit white, can you see it?" — was still being built and localised for
 * every step and discarded by the client; it is gone together with its
 * seventeen translation keys.
 */

/** Session state for the interactive segment-detection wizard */
export interface SegmentWizardSession {
  /** Target device key (sku:deviceId) */
  deviceKey: string;
  /** Device SKU for display */
  sku: string;
  /** Display name */
  name: string;
  /** Next segment index the wizard will flash */
  current: number;
  /** Upper bound — protocol limit, NOT Cloud-reported count */
  total: number;
  /** Indices confirmed visible by user */
  visible: number[];
  /** Timestamp of session start (for idle-timeout) */
  startedAt: number;
  /** Baseline snapshot for restore on abort/finish */
  baseline: {
    power?: boolean;
    brightness?: number;
    colorRgb?: string;
    segmentColors: { idx: number; color: string; brightness: number }[];
  };
}

/** Minimum response shape — onMessage forwards this verbatim to the caller. */
export type WizardResponse = Record<string, unknown>;

/**
 * Grid snapshot folded into every {@link SegmentWizard.runStep} response so the
 * React admin component can redraw the live segment map after each step. The
 * backend emits only `"idle"` (no session) / `"measuring"` (flashing); `"review"`
 * is a frontend-only phase — the React review screen derives its map from the
 * last measuring snapshot (confirmed indices + currentIndex).
 */
export interface WizardSnapshot {
  /** Session phase. Backend emits only `"idle"` / `"measuring"`. */
  phase: "idle" | "measuring" | "review";
  /** Upper bound of addressable segments (protocol limit, not Cloud count). */
  total: number;
  /** Index the wizard will flash next. */
  currentIndex: number;
  /** Indices the user has confirmed visible so far. */
  confirmed: number[];
}

/** Result of a completed wizard session — the host applies this to the device. */
export interface WizardResult {
  /** Real physical segment count (indices the user acknowledged, one past the last answered) */
  segmentCount: number;
  /** Gaps detected (indices marked dark between visible ones). Empty = contiguous strip. */
  manualList: string;
  /** Whether gaps were detected (manualList non-empty) */
  hasGaps: boolean;
}

/**
 * Everything the wizard needs from the outside world. Extracting this
 * interface keeps the wizard independent from the ioBroker base class and
 * makes the state machine testable without instantiating a full adapter.
 */
export interface WizardHost {
  /** Host logger (maps to `adapter.log`). */
  log: {
    debug(m: string): void;
    info(m: string): void;
    warn(m: string): void;
    error(m: string): void;
  };
  /** Read a state value by full ID. */
  getState(id: string): Promise<{ val: unknown } | null | undefined>;
  /** Dispatch a command to a device (normally DeviceManager.sendCommand). */
  sendCommand(device: GoveeDevice, command: string, value: unknown): Promise<void>;
  /**
   * Flash one segment bright white and dim all others — atomically, in a
   * single ptReal datagram. Required because separate UDP sends get dropped
   * by the Govee device under ~50 ms of back-pressure. Returns `true` if
   * the atomic path was used (LAN available), `false` if host fell back to
   * sendCommand (Cloud or no LAN).
   */
  flashSegmentAtomic(device: GoveeDevice, idx: number): Promise<boolean>;
  /**
   * Restore the whole strip to a uniform color + brightness atomically.
   * Returns `true` if LAN atomic path was used.
   */
  restoreStripAtomic(device: GoveeDevice, total: number, color: number, brightness: number): Promise<boolean>;
  /** Look up a device by its wizard-session key. */
  findDevice(key: string): GoveeDevice | undefined;
  /** Adapter namespace (e.g. "govee-smart.0"). */
  namespace: string;
  /** Derive the device's state-tree prefix (channel path below namespace). */
  devicePrefix(device: GoveeDevice): string;
  /** Schedule a managed timeout; must be cancellable via clearTimeout. */
  setTimeout: (cb: () => void, ms: number) => unknown;
  /** Cancel a previously scheduled timeout. */
  clearTimeout: (handle: unknown) => void;
  /**
   * Apply the finished wizard result to the device — sets segmentCount,
   * manualMode, manualSegments, rebuilds state tree, persists to cache.
   * Host owns this because it needs to coordinate with StateManager,
   * DeviceManager and SkuCache.
   */
  applyWizardResult(device: GoveeDevice, result: WizardResult): Promise<void>;
}

// IDLE_TIMEOUT_MS is now sourced from timing-constants (WIZARD_IDLE_TIMEOUT_MS).

/**
 * Check whether a device has any segment capability at all. A strip with
 * zero segments (e.g. Curtain H70B3) can't be wizard-tested.
 *
 * @param device Target device
 */
function hasSegmentCapability(device: GoveeDevice): boolean {
  const caps = Array.isArray(device.capabilities) ? device.capabilities : [];
  return caps.some(c => c && typeof c.type === "string" && c.type.includes("segment_color_setting"));
}

/**
 * Interactive segment-detection state machine.
 *
 * Flashes each segment bright white one-by-one up to the protocol limit.
 * The user steers the session with `yes` (visible) / `no` (dark) and ends it
 * with `apply` (the review-corrected map) or `abort`. Reaching the protocol
 * limit finalizes on its own. The final result — real segment count plus any
 * gaps for cut strips — is applied via {@link WizardHost.applyWizardResult}.
 *
 * Public entry point is {@link SegmentWizard.runStep} which routes by action
 * string ("start" | "yes" | "no" | "apply" | "abort"). Individual lifecycle
 * methods are also usable directly; they're the seams that tests target.
 */
export class SegmentWizard {
  private session: SegmentWizardSession | null = null;
  private timeoutHandle: unknown = undefined;

  /** @param host Host interface wired up to the adapter. */
  constructor(private readonly host: WizardHost) {}

  /**
   * Snapshot the active session — used by the diag-export runtime-state
   * provider. Returns null when no session is in flight. Plain object so
   * the DiagnosticsCollector can clone-and-cap it safely.
   */
  public getSessionSnapshot(): SegmentWizardSession | null {
    return this.session ? { ...this.session, visible: [...this.session.visible] } : null;
  }

  /**
   * Look up a localized wizard string (admin/i18n, resolved in the system
   * language via adapter-core I18n) and interpolate its `{name}` placeholders.
   *
   * @param key admin/i18n key
   * @param params Optional placeholder values for `{name}` slots in the template
   */
  private t(key: I18nKey, params?: Record<string, string | number>): string {
    return format(resolveLabel(key), params);
  }

  /**
   * Clear any pending idle-timer. Called from onUnload.
   *
   * If a wizard is still running on adapter stop the strip stays in its
   * white-flash state. UX trade-off — onUnload must be synchronous, so
   * restoreBaseline can't be awaited here. The user is already hinted at this
   * in the start log.
   */
  public dispose(): void {
    if (this.session) {
      // Best-effort restore on adapter-stop: fire-and-forget the baseline
      // restore so the strip doesn't stay in the wizard's test pattern (one
      // segment white, others off). onUnload itself runs synchronously, so
      // we can't await this — but a single LAN/ptReal datagram leaves the
      // adapter quickly enough that the os-level UDP send usually completes
      // before the process exits. If it doesn't, the user-visible state is
      // no worse than the previous "strip stuck in flash pattern" warning.
      const session = this.session;
      const device = this.host.findDevice(session.deviceKey);
      if (device) {
        try {
          const total = device.segmentCount ?? 0;
          if (total > 0 && session.baseline.colorRgb && /^#[0-9a-fA-F]{6}$/.test(session.baseline.colorRgb)) {
            const color = parseInt(session.baseline.colorRgb.slice(1), 16);
            const brightness = session.baseline.brightness ?? 100;
            // Fire-and-forget on teardown — the surrounding try/catch only
            // catches synchronous throws, so swallow the promise rejection too
            // to avoid an unhandled rejection during unload (L14).
            void this.host.restoreStripAtomic(device, total, color, brightness).catch(() => undefined);
          }
        } catch {
          // Last-mile error during teardown — nothing useful left to log.
        }
      }
      this.host.log.warn(
        "Segment wizard active during adapter stop — best-effort baseline restore sent. Run wizard 'done' or 'abort' next time for a clean finish.",
      );
    }
    this.clearIdleTimer();
    this.session = null;
  }

  /**
   * Route one wizard step from the sendTo handler and fold the current grid
   * snapshot into every response so the React admin map can redraw per step.
   *
   * @param action "start" | "yes" | "no" | "apply" | "abort"
   * @param deviceKey Target device — only consulted on action="start"
   * @param payload Extra data for actions that need it (apply: corrected indices)
   * @param payload.indices Review-corrected segment indices for the `apply` action
   */
  public async runStep(action: string, deviceKey: string, payload?: { indices?: number[] }): Promise<WizardResponse> {
    const response = await this.route(action, deviceKey, payload);
    return { ...response, snapshot: this.snapshot() };
  }

  /**
   * Dispatch a wizard action to its handler. The session-active guard covers
   * every action except `start` (which opens a session); `apply` shares that
   * guard with `yes`/`no`/`abort`.
   *
   * @param action Wizard action string
   * @param deviceKey Target device — only consulted on action="start"
   * @param payload Extra data (apply: corrected indices)
   * @param payload.indices Review-corrected segment indices for the `apply` action
   */
  private async route(action: string, deviceKey: string, payload?: { indices?: number[] }): Promise<WizardResponse> {
    if (action === "start") {
      return this.start(deviceKey);
    }
    if (!this.session) {
      return { error: this.t("errNoWizard") };
    }
    if (action === "abort") {
      return this.abort();
    }
    if (action === "apply") {
      return this.apply(payload?.indices ?? []);
    }
    if (action === "yes" || action === "no") {
      return this.answer(action === "yes");
    }
    return { error: this.t("errUnknownAction", { action }) };
  }

  /**
   * Shape the current session into a {@link WizardSnapshot} for the React grid.
   * Maps the internal session fields (`current` → `currentIndex`, `visible` →
   * `confirmed`) and returns an idle snapshot when no session is in flight.
   */
  private snapshot(): WizardSnapshot {
    const s = this.session;
    if (!s) {
      return { phase: "idle", total: 0, currentIndex: 0, confirmed: [] };
    }
    return {
      phase: "measuring",
      total: s.total,
      currentIndex: s.current,
      confirmed: [...s.visible],
    };
  }

  /**
   * Begin a new wizard session. Captures baseline and flashes segment 0.
   *
   * @param deviceKey Target device key
   */
  public async start(deviceKey: string): Promise<WizardResponse> {
    if (this.session) {
      return {
        error: this.t("errAlreadyActive", { name: this.session.name }),
      };
    }
    const device = this.host.findDevice(deviceKey);
    if (!device) {
      return { error: this.t("errDeviceNotFound", { key: deviceKey }) };
    }
    if (!hasSegmentCapability(device)) {
      return {
        error: this.t("errNoSegments", { name: device.name }),
      };
    }
    // Until 2.31.0 an unreachable device could not get this far: the wizard had
    // its own device list and that list filtered on reachability. Now one list
    // serves both halves of the Expert tab and the card filters it, so a device
    // that drops off between loading the list and pressing start would reach
    // this point — and the wizard would flash segments at something that cannot
    // answer, with nothing on screen to explain the silence.
    if (device.state?.online !== true) {
      return {
        error: this.t("errDeviceOffline", { name: device.name }),
      };
    }

    // Reserve the session slot SYNCHRONOUSLY — before the first `await`
    // (captureBaseline) — so a second start() from the fire-and-forget onMessage
    // path can't slip past the `this.session` guard above while the baseline is
    // still being captured and open a double session (TOCTOU, L6).
    const session: SegmentWizardSession = {
      deviceKey,
      sku: device.sku,
      name: device.name,
      current: 0,
      total: SEGMENT_COUNT_MAX,
      visible: [],
      startedAt: Date.now(),
      baseline: { segmentColors: [] },
    };
    this.session = session;

    session.baseline = await this.captureBaseline(device);
    if (this.session !== session) {
      // Aborted / replaced while we were capturing — don't stomp the newer state.
      return { error: this.t("errAlreadyActive", { name: this.session?.name ?? device.name }) };
    }
    this.scheduleIdleTimeout();
    // Make sure the strip is ON and at full global brightness before we
    // start flashing segments — otherwise a user with their strip dimmed to
    // e.g. 10 % would see nothing.
    await this.host.sendCommand(device, "power", true);
    await this.host.sendCommand(device, "brightness", 100);
    await this.flashSegment(device, 0);

    return { active: true };
  }

  /**
   * Record the user's answer for the current segment and advance.
   *
   * @param wasVisible Whether the user saw the flashed segment
   */
  public async answer(wasVisible: boolean): Promise<WizardResponse> {
    const session = this.session;
    if (!session) {
      return { error: this.t("errNoWizardShort") };
    }
    if (wasVisible) {
      session.visible.push(session.current);
    }
    session.current += 1;
    this.scheduleIdleTimeout();

    // Safety: protocol bitmask can only address 0..55. If we somehow get
    // there, auto-finalize instead of flashing a seg the device can't render.
    if (session.current > SEGMENT_HARD_MAX) {
      return this.finish();
    }

    const device = this.host.findDevice(session.deviceKey);
    if (!device) {
      this.session = null;
      this.clearIdleTimer();
      return { error: this.t("errDeviceGone") };
    }
    await this.flashSegment(device, session.current);
    return { active: true };
  }

  /** Abort the session and roll back to the captured baseline. */
  public async abort(): Promise<WizardResponse> {
    const session = this.session;
    if (!session) {
      return { error: this.t("errNoWizardShort") };
    }
    const device = this.host.findDevice(session.deviceKey);
    if (device) {
      await this.restoreBaseline(device, session.baseline);
    }
    this.session = null;
    this.clearIdleTimer();
    return { done: true, aborted: true };
  }

  /**
   * Consolidate the session into a {@link WizardResult}, hand off to the host
   * for application, restore baseline and close the session. Reached only when
   * the protocol limit is hit mid-measurement — the React flow finalizes via
   * {@link apply} with its review-corrected map.
   */
  private async finish(): Promise<WizardResponse> {
    const session = this.session;
    if (!session) {
      return { error: this.t("errNoWizardShort") };
    }
    const device = this.host.findDevice(session.deviceKey);
    if (!device) {
      this.session = null;
      this.clearIdleTimer();
      return { error: this.t("errDeviceGoneShort") };
    }

    const segmentCount = session.current;
    const visible = session.visible.slice().sort((a, b) => a - b);
    const allContiguous = visible.length === segmentCount && visible.every((v, i) => v === i);
    const manualList = allContiguous ? "" : compactIndices(visible);
    const result: WizardResult = {
      segmentCount,
      manualList,
      hasGaps: !allContiguous,
    };

    await this.finalize(device, session, result);

    return { done: true, segmentCount, list: manualList, hasGaps: result.hasGaps };
  }

  /**
   * Finalize the review-corrected segment map. The React review screen may have
   * toggled cells before the user hit "Übernehmen"; the corrected indices arrive
   * here and run through the SAME finalization as {@link finish} (apply →
   * restore baseline → close session). Requires an active session — routed only
   * after the session-active guard in {@link route} — and a present device.
   *
   * @param indices Segment indices the user confirmed as lit (0-based, unsorted)
   */
  private async apply(indices: number[]): Promise<WizardResponse> {
    const session = this.session;
    if (!session) {
      return { error: this.t("errNoWizardShort") };
    }
    // Only indices the bitmask protocol can address (0..SEGMENT_HARD_MAX) — the
    // payload comes straight from the admin socket, and an oversized index would
    // otherwise become the device's segment count and build that many channels.
    const clean = [...new Set(indices.filter(i => Number.isInteger(i) && i >= 0 && i <= SEGMENT_HARD_MAX))].sort(
      (a, b) => a - b,
    );
    if (clean.length === 0) {
      // Nothing selected — the UI prevents this, but never build a -Infinity
      // segment count from an empty map. Keep the session so the user can retry.
      return { error: this.t("errAnswerFirst") };
    }
    const device = this.host.findDevice(session.deviceKey);
    if (!device) {
      this.session = null;
      this.clearIdleTimer();
      return { error: this.t("errDeviceGoneShort") };
    }

    const segmentCount = clean[clean.length - 1] + 1;
    const hasGaps = clean.length < segmentCount;
    const manualList = hasGaps ? compactIndices(clean) : "";
    const result: WizardResult = { segmentCount, manualList, hasGaps };

    await this.finalize(device, session, result);

    return { applied: true, segmentCount, list: manualList, hasGaps };
  }

  /**
   * Apply a consolidated result to the device, restore the strip's baseline,
   * log the outcome and close the session. Shared side-effect path for
   * {@link finish} (measured indices) and {@link apply} (review-corrected
   * indices). Reads `session.baseline` before the session is nulled.
   *
   * @param device Target device
   * @param session The active session being finalized
   * @param result Consolidated wizard result to apply
   */
  private async finalize(device: GoveeDevice, session: SegmentWizardSession, result: WizardResult): Promise<void> {
    await this.host.applyWizardResult(device, result);
    await this.restoreBaseline(device, session.baseline);

    // Logs are English regardless of admin language (system-language rule); the
    // user-facing `message`/`progress` stay localized (C6).
    const gapsSuffix = result.hasGaps ? `, gaps detected (manual_list="${result.manualList}")` : ", no gaps";
    this.host.log.info(
      `Segment wizard for ${deviceLabel(device)}: ${result.segmentCount} segments detected${gapsSuffix}`,
    );

    this.session = null;
    this.clearIdleTimer();
  }

  /** (Re-)arm the 5-minute idle timeout that fires abort(). */
  private scheduleIdleTimeout(): void {
    this.clearIdleTimer();
    this.timeoutHandle = this.host.setTimeout(() => {
      if (!this.session) {
        return;
      }
      this.host.log.warn(`Segment wizard for ${deviceLabel(this.session)}: idle timeout (5 min), aborted`);
      this.abort().catch(e => {
        this.host.log.warn(
          this.t("logAbortFailed", {
            msg: e instanceof Error ? e.message : String(e),
          }),
        );
        this.session = null;
      });
    }, WIZARD_IDLE_TIMEOUT_MS);
  }

  /** Cancel the idle timer without running its callback. */
  private clearIdleTimer(): void {
    if (this.timeoutHandle !== undefined) {
      this.host.clearTimeout(this.timeoutHandle);
      this.timeoutHandle = undefined;
    }
  }

  /**
   * Snapshot the device's current power/brightness/colorRgb plus per-segment
   * color+brightness so the baseline can be restored on abort/finish.
   *
   * @param device Target device
   */
  private async captureBaseline(device: GoveeDevice): Promise<SegmentWizardSession["baseline"]> {
    // Read control + per-segment state in parallel (one round-trip vs
    // segmentCount × 2 sequential reads). Unlit segments default to white.
    const base = await readDeviceBaseline(this.host, device, { color: "#ffffff", brightness: 100 });
    return {
      power: base.power,
      brightness: base.brightness,
      colorRgb: base.colorRgb,
      segmentColors: base.segments.map((s, idx) => ({ idx, color: s.color, brightness: s.brightness })),
    };
  }

  /**
   * Flash one segment bright white, dimming all others so only the target is
   * clearly visible.
   *
   * @param device Target device
   * @param idx Segment to flash white (others go near-black)
   */
  private async flashSegment(device: GoveeDevice, idx: number): Promise<void> {
    const atomic = await this.host.flashSegmentAtomic(device, idx);
    if (atomic) {
      return;
    }
    // Fallback (Cloud or no LAN): two sendCommand calls with pacing.
    // Fallback drives the full protocol range — we can't know the real count
    // yet. The device silently drops indices it doesn't physically have.
    const total = SEGMENT_COUNT_MAX;
    const others = Array.from({ length: total }, (_, i) => i).filter(i => i !== idx);
    if (others.length > 0) {
      await this.host.sendCommand(device, "segmentBatch", {
        segments: others,
        color: 0,
        brightness: 0,
      });
    }
    await this.host.sendCommand(device, "segmentBatch", {
      segments: [idx],
      color: 0xffffff,
      brightness: 100,
    });
  }

  /**
   * Send one segmentBatch that pushes the captured baseline back onto the
   * whole strip. No-op when no RGB baseline was captured (e.g. fresh state).
   *
   * @param device Target device
   * @param baseline Previously captured baseline values
   */
  private async restoreBaseline(device: GoveeDevice, baseline: SegmentWizardSession["baseline"]): Promise<void> {
    const total = device.segmentCount ?? 0;
    const segs = baseline.segmentColors;
    // Prefer the real per-segment gradient when one was genuinely captured: a
    // FULL set of segments (matching the current count) carrying ≥2 distinct
    // (colour, brightness) tuples. A strip that was never in gradient/colour
    // mode has no reliable per-segment state — its segments read the def and
    // collapse to a single tuple, so we fall back to the uniform colorRgb
    // instead of painting the def across the whole strip (L8, gradient part).
    const distinctTuples = new Set(segs.map(s => `${s.color}:${s.brightness}`)).size;
    if (total > 0 && segs.length === total && distinctTuples >= 2) {
      // Shared grouped replay (device-baseline.ts) — same path as the
      // local-snapshot restore, one segmentBatch per distinct tuple.
      await restoreSegmentsGrouped((d, cmd, val) => this.host.sendCommand(d, cmd, val), device, segs);
    } else if (baseline.colorRgb && /^#[0-9a-fA-F]{6}$/.test(baseline.colorRgb) && total > 0) {
      const color = parseInt(baseline.colorRgb.slice(1), 16);
      const brightness = baseline.brightness ?? 100;
      const atomic = await this.host.restoreStripAtomic(device, total, color, brightness);
      if (!atomic) {
        await this.host.sendCommand(device, "segmentBatch", {
          segments: Array.from({ length: total }, (_, i) => i),
          color,
          brightness,
        });
      }
    }
    // Restore the original power state — the wizard forces the strip ON to flash
    // segments (start() sends power:true), so a device that was OFF beforehand
    // must be turned back off, otherwise the wizard silently leaves it on (L8).
    if (baseline.power === false) {
      await this.host.sendCommand(device, "power", false);
    }
  }
}

/**
 * Compact a sorted index array into a range-notation string.
 * `[0,1,2,4,5,6]` → `"0-2,4-6"`, `[3]` → `"3"`, `[]` → `""`.
 *
 * @param sorted Sorted ascending array of non-negative integers
 */
function compactIndices(sorted: number[]): string {
  if (sorted.length === 0) {
    return "";
  }
  const parts: string[] = [];
  let runStart = sorted[0];
  let runEnd = sorted[0];
  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i] === runEnd + 1) {
      runEnd = sorted[i];
    } else {
      parts.push(runStart === runEnd ? `${runStart}` : `${runStart}-${runEnd}`);
      runStart = sorted[i];
      runEnd = sorted[i];
    }
  }
  parts.push(runStart === runEnd ? `${runStart}` : `${runStart}-${runEnd}`);
  return parts.join(",");
}
